/**
 * Authentication Service
 * Handles authentication with various providers including Google OAuth and Active Directory
 */

export type AuthProvider = 'local' | 'google' | 'microsoft';

export interface OAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
}

export interface AuthResult {
  success: boolean;
  provider: AuthProvider;
  email?: string;
  name?: string;
  error?: string;
}

/**
 * Get Google OAuth configuration from environment variables
 */
export const getGoogleOAuthConfig = (): OAuthConfig | null => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    console.warn('Google OAuth is not configured. Set VITE_GOOGLE_CLIENT_ID in .env file');
    return null;
  }
  
  return {
    clientId,
    redirectUri: window.location.origin + '/login',
    scope: 'openid email profile'
  };
};

/**
 * Get Microsoft/Active Directory OAuth configuration from environment variables
 */
export const getMicrosoftOAuthConfig = (): OAuthConfig | null => {
  const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
  if (!clientId) {
    console.warn('Microsoft OAuth is not configured. Set VITE_MICROSOFT_CLIENT_ID in .env file');
    return null;
  }
  
  return {
    clientId,
    redirectUri: window.location.origin + '/login',
    scope: 'openid email profile'
  };
};

/**
 * Initiate Google OAuth sign-in
 * This opens the Google OAuth consent screen
 */
export const signInWithGoogle = async (): Promise<AuthResult> => {
  const config = getGoogleOAuthConfig();
  
  if (!config) {
    return {
      success: false,
      provider: 'google',
      error: 'Google OAuth is not configured'
    };
  }

  // Build Google OAuth URL
  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.append('client_id', config.clientId);
  authUrl.searchParams.append('redirect_uri', config.redirectUri);
  authUrl.searchParams.append('response_type', 'token');
  authUrl.searchParams.append('scope', config.scope);
  authUrl.searchParams.append('state', 'google_oauth');

  // For demo purposes, simulate OAuth flow
  // In production, this would redirect to Google OAuth consent screen
  console.log('Google OAuth URL:', authUrl.toString());
  
  // Simulate successful OAuth response for demo
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        provider: 'google',
        email: 'demo.user@gmail.com',
        name: 'Demo Google User'
      });
    }, 1000);
  });
};

/**
 * Initiate Microsoft/Active Directory OAuth sign-in
 * This opens the Microsoft OAuth consent screen
 */
export const signInWithMicrosoft = async (): Promise<AuthResult> => {
  const config = getMicrosoftOAuthConfig();
  
  if (!config) {
    return {
      success: false,
      provider: 'microsoft',
      error: 'Microsoft OAuth is not configured'
    };
  }

  const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common';

  // Build Microsoft OAuth URL
  const authUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
  authUrl.searchParams.append('client_id', config.clientId);
  authUrl.searchParams.append('redirect_uri', config.redirectUri);
  authUrl.searchParams.append('response_type', 'token');
  authUrl.searchParams.append('scope', config.scope);
  authUrl.searchParams.append('state', 'microsoft_oauth');

  // For demo purposes, simulate OAuth flow
  // In production, this would redirect to Microsoft OAuth consent screen
  console.log('Microsoft OAuth URL:', authUrl.toString());
  
  // Simulate successful OAuth response for demo
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        provider: 'microsoft',
        email: 'msanders@yourcompany.com',
        name: 'Michelle Sanders'
      });
    }, 1000);
  });
};

/**
 * Parse OAuth callback and extract user information
 * This would be called when the OAuth provider redirects back to the app
 */
export const handleOAuthCallback = (): AuthResult | null => {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  
  const accessToken = params.get('access_token');
  const state = params.get('state');
  
  if (!accessToken || !state) {
    return null;
  }
  
  // Determine provider from state
  const provider: AuthProvider = state.includes('google') ? 'google' : 
                                  state.includes('microsoft') ? 'microsoft' : 'local';
  
  // In production, you would use the access token to fetch user info from the provider
  // For now, return a successful result
  return {
    success: true,
    provider
  };
};
